/**
 * WatchlistThesisForm - capture/edit a structured investment thesis for a
 * watchlist item. Fields: duration strategy, entry_price (prefilled
 * from current price), remind_at, statement, pillars, risks.
 * No stop-loss/take-profit (watchlist-specific); no conviction-level picker
 * (parity with PositionThesisForm, which carries conviction through without
 * exposing it). Shares types/i18n with PositionThesisForm but is
 * watchlist-bound. Layout and margins mirror PositionThesisForm so both
 * thesis edit screens look identical.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { DatePicker } from '../ui/DatePicker';
import { LongTextField } from '../ui/LongTextField';
import { showSuccess, showError } from '../../utils/alert';
import { useTranslation } from 'react-i18next';
import {
  useThemeTags,
  useThemeRegistry,
  registryToMap,
} from '../../services/api/queries/useThemeTags';
import { translateTheme } from '../../utils/translationHelpers';
import {
  useWatchlistThesis,
  useAddWatchlistThesis,
} from '../../services/queries/usePositionTheses';
import { updatePositionThesis } from '../../services/api/positionThesisApi';
import { createSession } from '../../services/chatApi';
import type { ChatSession } from '../../services/chatApi';
import type { ThesisCommand } from '../../services/chatStream';
import { parseThesisText, StructuredThesis, THESIS_LIST_ITEM_MAX_LENGTH } from '../../types/thesis';

// Strategy tab configuration - same as PositionThesisForm
interface StrategyOption {
  value: 'Core' | 'Growth' | 'Speculative';
  labelKey: string;
  defaultLabel: string;
  subtitleKey: string;
  defaultSubtitle: string;
  icon: keyof typeof Ionicons.glyphMap;
}

const STRATEGY_OPTIONS: StrategyOption[] = [
  {
    value: 'Core',
    labelKey: 'portfolio.durationCoreHolding',
    defaultLabel: 'Core',
    subtitleKey: 'portfolio.durationCoreHoldingDesc',
    defaultSubtitle: '3+ Years',
    icon: 'shield-outline',
  },
  {
    value: 'Growth',
    labelKey: 'portfolio.durationMidTerm',
    defaultLabel: 'Growth',
    subtitleKey: 'portfolio.durationMidTermDesc',
    defaultSubtitle: '1-2 Years',
    icon: 'trending-up',
  },
  {
    value: 'Speculative',
    labelKey: 'portfolio.durationShortTerm',
    defaultLabel: 'Speculative',
    subtitleKey: 'portfolio.durationShortTermDesc',
    defaultSubtitle: 'Weeks/Months',
    icon: 'flash',
  },
];

export interface WatchlistThesisFormProps {
  watchlistItemId: number;
  symbol: string;
  instrumentName?: string;
  currentPrice?: number | null;
  onCancel: () => void;
  onSaved: () => void;
  onNavigateToChat?: (params: {
    sessionId?: string;
    initialMessage?: string;
    autoSend?: boolean;
    initialCommand?: ThesisCommand;
  }) => void;
}

export function WatchlistThesisForm({
  watchlistItemId,
  symbol,
  instrumentName,
  currentPrice,
  onCancel,
  onSaved,
  onNavigateToChat,
}: WatchlistThesisFormProps) {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const themesQuery = useThemeTags(symbol);
  const registryQuery = useThemeRegistry();
  const themeRegistryMap = registryToMap(registryQuery.data);
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<any>();

  const { data: existing, isLoading } = useWatchlistThesis(watchlistItemId);
  const addThesis = useAddWatchlistThesis();

  const [statement, setStatement] = useState('');
  const [pillars, setPillars] = useState<string[]>([]);
  const [risks, setRisks] = useState<string[]>([]);
  const [conviction, setConviction] = useState<number | null>(null);
  const [duration, setDuration] = useState<'Core' | 'Growth' | 'Speculative' | null>(null);
  const [entryPrice, setEntryPrice] = useState(currentPrice != null ? String(currentPrice) : '');
  const [remindAt, setRemindAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const queryClient = useQueryClient();

  // Prefill from the latest existing version once.
  useEffect(() => {
    if (loaded || isLoading || !existing || existing.length === 0) return;
    const latest = existing[0];
    const parsed = parseThesisText(latest.thesis_text);
    setStatement(parsed.statement);
    setPillars(parsed.pillars);
    setRisks(parsed.risks);
    setConviction(latest.conviction_level);
    setDuration(latest.duration);
    if (latest.entry_price != null) setEntryPrice(String(Number(latest.entry_price)));
    if (latest.remind_at) setRemindAt(latest.remind_at);
    setLoaded(true);
  }, [existing, isLoading, loaded]);

  // ── Build with TopNote handler (spec 3.2 entry A) ──
  // Creates a fresh, titled chat session and seeds it with a structured
  // build_thesis command: symbol, this watchlist item as target, and — when a
  // thesis already exists — the latest saved version as the seed snapshot.
  const handleBuildWithTopNote = useCallback(async () => {
    if (!symbol) return;
    try {
      setBuilding(true);
      const session = await createSession(`📐 ${symbol} thesis`);
      // Seed the sessions cache BEFORE navigating. This is a raw createSession
      // (not the chat shell's mutation), so without this the
      // ['chat-sessions','active'] cache never learns the session exists and
      // useChatSessions' vanish-check "self-heals" the selection onto an old
      // session on arrival — dropping the build_thesis command (blank session
      // until a manual refresh; bug A, 2026-10-04). Invalidate behind the seed
      // so the list also refetches with the created row.
      queryClient.setQueryData<ChatSession[]>(['chat-sessions', 'active'], (old) =>
        old ? [session, ...old] : [session]
      );
      void queryClient.invalidateQueries({ queryKey: ['chat-sessions', 'active'] });
      const latest = existing && existing.length > 0 ? existing[0] : null;
      const parsed = latest ? parseThesisText(latest.thesis_text) : null;
      const seed =
        latest && parsed
          ? {
              statement: parsed.statement,
              pillars: parsed.pillars,
              risks: parsed.risks,
              conviction_level: latest.conviction_level,
              duration: latest.duration,
              entry_price: latest.entry_price != null ? Number(latest.entry_price) : null,
              // Must ride along: thesis-save writes these unconditionally, so
              // a seed missing them would ERASE them on the seeded save.
              stop_loss_price:
                latest.stop_loss_price != null ? Number(latest.stop_loss_price) : null,
              take_profit_price:
                latest.take_profit_price != null ? Number(latest.take_profit_price) : null,
              remind_at: latest.remind_at,
            }
          : null;
      const navParams = {
        sessionId: session.id,
        initialCommand: {
          command: 'build_thesis' as const,
          symbol,
          target: { kind: 'watchlist_item' as const, id: watchlistItemId },
          seed_snapshot: seed,
        },
      };
      if (onNavigateToChat) onNavigateToChat(navParams);
      else navigation.navigate('Chat', { screen: 'ChatScreen', params: navParams });
    } catch (e) {
      showError((e as Error).message);
    } finally {
      setBuilding(false);
    }
  }, [symbol, existing, watchlistItemId, navigation, onNavigateToChat, queryClient]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  const hasExisting = !!existing && existing.length > 0;

  // Helper functions for managing pillars
  const addPillar = () => {
    if (pillars.length >= 10) return;
    setPillars([...pillars, '']);
  };

  const updatePillar = (index: number, value: string) => {
    const newPillars = [...pillars];
    newPillars[index] = value;
    setPillars(newPillars);
  };

  const removePillar = (index: number) => {
    setPillars(pillars.filter((_, i) => i !== index));
  };

  // Helper functions for managing risks
  const addRisk = () => {
    if (risks.length >= 10) return;
    setRisks([...risks, '']);
  };

  const updateRisk = (index: number, value: string) => {
    const newRisks = [...risks];
    newRisks[index] = value;
    setRisks(newRisks);
  };

  const removeRisk = (index: number) => {
    setRisks(risks.filter((_, i) => i !== index));
  };

  const onSave = async () => {
    if (!statement.trim()) {
      showError(
        t('watchlist.thesisStatementRequired', { defaultValue: 'A thesis statement is required.' })
      );
      return;
    }
    setSaving(true);
    try {
      const structured: StructuredThesis = { statement: statement.trim(), pillars, risks };
      const entryNum = entryPrice.trim() === '' ? null : Number(entryPrice);
      if (hasExisting && existing) {
        await updatePositionThesis(existing[0].id, {
          structured_thesis: structured,
          conviction_level: conviction,
          duration,
          entry_price: entryNum,
          remind_at: remindAt,
        });
      } else {
        await addThesis.mutateAsync({
          watchlist_item_id: watchlistItemId,
          structured_thesis: structured,
          conviction_level: conviction,
          duration,
          entry_price: entryNum,
          remind_at: remindAt,
        });
      }
      showSuccess(t('watchlist.thesisSaved', { defaultValue: 'Thesis saved.' }));
      onSaved();
    } catch (e) {
      showError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollView
      style={styles.outerContainer}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingBottom: 300 }}
    >
      <Card style={styles.thesisCard}>
        {/* Title Row with AI Buttons 鈥?same pattern as PositionThesisForm */}
        <View style={styles.titleRow}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {symbol}
            {instrumentName ? ` - ${instrumentName}` : ''}
          </Text>

          {symbol && (
            <View style={styles.aiButtonsRow}>
              <TouchableOpacity
                testID="chat-about-it-button"
                style={[styles.aiButton, { borderColor: theme.colors.primary }]}
                onPress={handleBuildWithTopNote}
                disabled={saving || building}
                activeOpacity={0.7}
              >
                {building ? (
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                ) : (
                  <Ionicons name="chatbubble-outline" size={14} color={theme.colors.primary} />
                )}
                <Text style={[styles.aiButtonText, { color: theme.colors.primary }]}>
                  {t('portfolio.buildWithTopNote', { defaultValue: 'Build with TopNote' })}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Conviction Strategy Tabs */}
        <View style={styles.fieldGroup}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.convictionStrategy', { defaultValue: 'Conviction Strategy' })}
          </Text>
          <View style={styles.strategyTabsContainer}>
            {STRATEGY_OPTIONS.map((option) => {
              const isSelected = duration === option.value;
              return (
                <TouchableOpacity
                  key={option.value}
                  testID={`strategy-${option.value}`}
                  style={[
                    styles.strategyTab,
                    {
                      borderColor: isSelected ? theme.colors.primary : theme.colors.border,
                      backgroundColor: isSelected
                        ? theme.colors.primary + '18'
                        : theme.colors.backgroundElevated,
                      borderWidth: isSelected ? 2 : theme.id === 'bauhaus' ? 2 : 1,
                    },
                  ]}
                  onPress={() => setDuration(isSelected ? null : option.value)}
                  disabled={saving}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={option.icon}
                    size={20}
                    color={isSelected ? theme.colors.primary : theme.colors.textTertiary}
                    style={styles.strategyTabIcon}
                  />
                  <Text
                    style={[
                      styles.strategyTabLabel,
                      { color: isSelected ? theme.colors.primary : theme.colors.text },
                    ]}
                  >
                    {t(option.labelKey, { defaultValue: option.defaultLabel })}
                  </Text>
                  <Text
                    style={[
                      styles.strategyTabSubtitle,
                      { color: isSelected ? theme.colors.primary : theme.colors.textTertiary },
                    ]}
                  >
                    {t(option.subtitleKey, { defaultValue: option.defaultSubtitle })}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Research themes (spec 006 D5): read-only pills from the wiki registry */}
        <View style={styles.fieldGroup}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
            {t('portfolio.stockThemesLabel', { defaultValue: 'Research themes' })}
          </Text>
          {themesQuery.isLoading ? (
            <ActivityIndicator size="small" color={theme.colors.primary} />
          ) : themesQuery.isError ? (
            <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.stockThemesUnavailable', {
                defaultValue: 'Theme registry unavailable',
              })}
            </Text>
          ) : (themesQuery.data?.themes ?? []).length === 0 ? (
            <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
              {t('portfolio.stockThemesEmpty', { defaultValue: 'Not mapped to any theme yet' })}
            </Text>
          ) : (
            <View style={styles.themePillWrap}>
              {(themesQuery.data?.themes ?? []).map((tag) => (
                <View
                  key={`${tag.theme_id}`}
                  style={[
                    styles.themePill,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor:
                        tag.role === 'primary' ? theme.colors.primary : theme.colors.border,
                    },
                  ]}
                >
                  <Text style={[styles.themePillText, { color: theme.colors.text }]}>
                    {translateTheme(tag.slug, themeRegistryMap, i18n.language)}
                  </Text>
                  {tag.role === 'primary' && (
                    <Text style={[styles.themePillBadge, { color: theme.colors.primary }]}>
                      {t('portfolio.themePrimaryBadge', { defaultValue: 'Primary' })}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          )}
          <Text style={[styles.helperText, { color: theme.colors.textTertiary }]}>
            {t('portfolio.stockThemesHelp', {
              defaultValue: 'Themes come from the research registry and cannot be edited here.',
            })}
          </Text>
        </View>

        {/* Entry Price and Reminder Date - Side by Side */}
        <View style={styles.rowFieldGroup}>
          <View style={styles.halfField}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('watchlist.entryPrice', { defaultValue: 'Entry price' })}
            </Text>
            <TextInput
              testID="entry-price"
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              value={entryPrice}
              onChangeText={setEntryPrice}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={theme.colors.textTertiary}
              editable={!saving}
            />
          </View>

          <View style={styles.halfField}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('watchlist.reminderDate', { defaultValue: 'Reminder date' })}
            </Text>
            <DatePicker testID="reminder-date" value={remindAt} onChange={setRemindAt} />
          </View>
        </View>

        {/* Structured Thesis Sections */}
        <View style={styles.fieldGroup}>
          {/* Thesis Statement Section */}
          <View style={styles.thesisSection}>
            <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
              {t('portfolio.thesisStatement', { defaultValue: 'Thesis Statement' })}
            </Text>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.thesisStatementHint', {
                defaultValue: 'Summarize your investment thesis in one paragraph.',
              })}
            </Text>
            <TextInput
              testID="thesis-statement"
              style={[
                styles.textArea,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={t('portfolio.thesisStatementPlaceholder', {
                defaultValue:
                  "e.g., 'NVDA is a strong buy due to their AI dominance, growing data center revenue, and expanding software ecosystem.'",
              })}
              placeholderTextColor={theme.colors.textTertiary}
              value={statement}
              onChangeText={setStatement}
              multiline
              numberOfLines={4}
              maxLength={1000}
              editable={!saving}
            />
            <View style={styles.characterCount}>
              <Text style={[styles.characterCountText, { color: theme.colors.textTertiary }]}>
                {statement.length} / 1000
              </Text>
            </View>
          </View>

          {/* Key Pillars Section */}
          <View style={styles.thesisSection}>
            <View style={styles.sectionTitleRow}>
              <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
                {t('portfolio.keyPillars', { defaultValue: 'Key Pillars' })}
              </Text>
              <Text style={[styles.sectionCount, { color: theme.colors.textSecondary }]}>
                {pillars.length} / 10
              </Text>
            </View>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.keyPillarsHint', {
                defaultValue: 'List the supporting arguments for your thesis.',
              })}
            </Text>

            {pillars.map((pillar, index) => (
              <View key={`pillar-${index}`} style={styles.listItemRow}>
                <View style={styles.listItemInputWrapper}>
                  <LongTextField
                    testID={`pillar-input-${index}`}
                    value={pillar}
                    onChangeText={(text) => updatePillar(index, text)}
                    placeholder={t('portfolio.pillarPlaceholder', {
                      defaultValue: `e.g., 'AI market leadership'`,
                    })}
                    modalTitle={t('portfolio.editPillar', { defaultValue: 'Edit Pillar' })}
                    maxLength={THESIS_LIST_ITEM_MAX_LENGTH}
                    editable={!saving}
                  />
                </View>
                <TouchableOpacity
                  testID={`remove-pillar-${index}`}
                  style={[styles.removeButton, { borderColor: theme.colors.error }]}
                  onPress={() => removePillar(index)}
                  disabled={saving}
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                </TouchableOpacity>
              </View>
            ))}

            {pillars.length < 10 && (
              <TouchableOpacity
                testID="add-pillar-button"
                style={[styles.addButton, { borderColor: theme.colors.primary }]}
                onPress={addPillar}
                disabled={saving}
              >
                <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                  {t('portfolio.addPillar', { defaultValue: 'Add Pillar' })}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Key Risks Section */}
          <View style={styles.thesisSection}>
            <View style={styles.sectionTitleRow}>
              <Text style={[styles.sectionHeader, { color: theme.colors.text }]}>
                {t('portfolio.keyRisks', { defaultValue: 'Key Risks' })}
              </Text>
              <Text style={[styles.sectionCount, { color: theme.colors.textSecondary }]}>
                {risks.length} / 10
              </Text>
            </View>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
              {t('portfolio.keyRisksHint', {
                defaultValue: 'List the key risks that could invalidate your thesis.',
              })}
            </Text>

            {risks.map((risk, index) => (
              <View key={`risk-${index}`} style={styles.listItemRow}>
                <View style={styles.listItemInputWrapper}>
                  <LongTextField
                    testID={`risk-input-${index}`}
                    value={risk}
                    onChangeText={(text) => updateRisk(index, text)}
                    placeholder={t('portfolio.riskPlaceholder', {
                      defaultValue: `e.g., 'China export restrictions'`,
                    })}
                    modalTitle={t('portfolio.editRisk', { defaultValue: 'Edit Risk' })}
                    maxLength={THESIS_LIST_ITEM_MAX_LENGTH}
                    editable={!saving}
                  />
                </View>
                <TouchableOpacity
                  testID={`remove-risk-${index}`}
                  style={[styles.removeButton, { borderColor: theme.colors.error }]}
                  onPress={() => removeRisk(index)}
                  disabled={saving}
                >
                  <Ionicons name="close-circle" size={20} color={theme.colors.error} />
                </TouchableOpacity>
              </View>
            ))}

            {risks.length < 10 && (
              <TouchableOpacity
                testID="add-risk-button"
                style={[styles.addButton, { borderColor: theme.colors.primary }]}
                onPress={addRisk}
                disabled={saving}
              >
                <Ionicons name="add-circle" size={20} color={theme.colors.primary} />
                <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>
                  {t('portfolio.addRisk', { defaultValue: 'Add Risk' })}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.cancelButton, { borderColor: theme.colors.border, minHeight: 48 }]}
            onPress={onCancel}
            disabled={saving}
          >
            <Text style={[styles.cancelButtonText, { color: theme.colors.textSecondary }]}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Text>
          </TouchableOpacity>
          <Button
            title={
              saving
                ? t('common.saving', { defaultValue: 'Saving\u2026' })
                : t('common.save', { defaultValue: 'Save' })
            }
            onPress={onSave}
            disabled={saving}
            loading={saving}
          />
        </View>
      </Card>
    </ScrollView>
  );
}

/**
 * Create themed styles dynamically 鈥?mirrors PositionThesisForm so both
 * thesis edit screens share identical layout and margins.
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    outerContainer: {
      flex: 1,
    },
    thesisCard: {
      margin: theme.spacing.lg,
      padding: theme.spacing.lg,
    },
    titleRow: {
      marginBottom: theme.spacing.lg,
      flexDirection: 'row' as 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    aiButtonsRow: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.sm,
      flexShrink: 0,
    },
    aiButton: {
      flexDirection: 'row' as 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      gap: 4,
    },
    aiButtonText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.md,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    fieldGroup: {
      marginBottom: theme.spacing.lg,
    },
    rowFieldGroup: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    halfField: {
      flex: 1,
      minWidth: 0,
    },
    strategyTabsContainer: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.md,
    },
    strategyTab: {
      flex: 1,
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.radii.md,
    },
    strategyTabIcon: {
      marginBottom: theme.spacing.xs,
    },
    strategyTabLabel: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? ('uppercase' as 'uppercase') : ('none' as 'none'),
      letterSpacing: theme.id === 'bauhaus' ? -0.3 : 0,
    },
    strategyTabSubtitle: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 2,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
      minWidth: 0,
    },
    textArea: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
      minHeight: 100,
      textAlignVertical: 'top',
      color: theme.colors.text,
    },
    helperText: {
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textTertiary,
      marginTop: theme.spacing.xs,
    },
    characterCount: {
      alignItems: 'flex-end',
      marginTop: theme.spacing.xs,
    },
    characterCountText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
    },
    themePillWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    themePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    themePillBadge: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    themePillText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 32,
    },
    actions: {
      flexDirection: 'row' as 'row',
      gap: theme.spacing.md,
      marginTop: theme.spacing.lg,
    },
    cancelButton: {
      flex: 1,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: 14,
      paddingHorizontal: 20,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      minWidth: 120,
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    // Structured thesis section styles
    thesisSection: {
      marginBottom: theme.spacing.xl,
    },
    sectionTitleRow: {
      flexDirection: 'row' as 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xs,
    },
    sectionHeader: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.3 : 0,
    },
    sectionCount: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    addButton: {
      flexDirection: 'row' as 'row',
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      marginTop: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    addButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    listItemRow: {
      flexDirection: 'row' as 'row',
      alignItems: 'center' as 'center',
      marginBottom: theme.spacing.sm,
      gap: theme.spacing.sm,
    },
    listItemInputWrapper: {
      flex: 1,
    },
    removeButton: {
      padding: theme.spacing.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.sm,
      alignItems: 'center' as 'center',
      justifyContent: 'center' as 'center',
      minWidth: 36,
      minHeight: 36,
    },
  });
}
